<template>
  <div v-if="localSettings">
    <div class="text-caption text-grey-8 q-mb-md">
      Configure up to 5 quick-scouting tags. These allow coaches to rapidly evaluate player
      execution with a single tap in the Action HUD.
    </div>

    <q-list bordered class="bg-white rounded-borders">
      <q-expansion-item default-opened group="scouting" header-class="text-primary">
        <template v-slot:header>
          <q-item-section>
            <div class="text-subtitle2 text-weight-bold text-uppercase">Scouting Tags</div>
          </q-item-section>
          <q-item-section side @click.stop>
            <q-toggle
              :model-value="!isHidden('hiddenDimensions', 'scouting')"
              @update:model-value="toggleArrayItem('hiddenDimensions', 'scouting')"
              color="positive"
              label="Enable Scouting UI"
              left-label
              size="sm"
            />
          </q-item-section>
        </template>

        <q-separator />

        <q-card class="bg-grey-1">
          <q-card-section>
            <div
              v-for="tag in settingsStore._rawScoutingTags"
              :key="tag.value"
              class="row wrap items-center q-col-gutter-sm q-mb-sm"
            >
              <div class="col-12 col-sm-6">
                <q-input
                  :model-value="localSettings.customLabels[tag.value] || tag.label"
                  @update:model-value="
                    (val: string | number | null) => setCustomLabel(tag.value, val)
                  "
                  label="Tag Label"
                  outlined
                  dense
                  bg-color="white"
                />
              </div>
              <div class="col-12 col-sm-4">
                <q-select
                  :model-value="localSettings.customCategories?.[tag.value] || tag.category"
                  @update:model-value="(val: string) => setCustomCategory(tag.value, val)"
                  :options="['positive', 'negative', 'neutral']"
                  label="Category"
                  outlined
                  dense
                  bg-color="white"
                />
              </div>
              <div class="col-12 col-sm-2">
                <q-toggle
                  :model-value="!isHidden('hiddenScoutingTags', tag.value)"
                  @update:model-value="toggleArrayItem('hiddenScoutingTags', tag.value)"
                  label="Visible"
                  color="primary"
                  dense
                />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-expansion-item>
    </q-list>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from 'stores/auth-store'
import { useSettingsStore } from 'stores/settings-store'
import { supabase } from 'src/services/supabase'
import type { UserSettings } from 'stores/auth-store-types'

// Define optional props for cloud syncing
const props = defineProps<{
  gameId?: string
  coachInitials?: string
}>()

const authStore = useAuthStore()
const settingsStore = useSettingsStore()

const localSettings = ref<UserSettings | null>(null)

// Fallback defaults if the user has a fresh profile
const defaultSettings: UserSettings = {
  defaultInnings: 6,
  defaultRosterSize: 12,
  useDesignatedHitter: true,
  themePreference: 'system',
  enablePitchQuickLog: true,
  enablePitchCategories: true,
  enablePitchTypes: true,
  enablePitchQuality: true,
  useReadablePlayStatus: false,
  autoAdvanceLeadRunner: true,
  hiddenPitchOptions: [],
  hiddenApproachOptions: [],
  hiddenExecutionOptions: [],
  hiddenTransferOptions: [],
  hiddenThrowOptions: [],
  hiddenDecisionOptions: [],
  hiddenQualityOptions: [],
  hiddenDimensions: [],
  hiddenRunnerOptions: [],
  hiddenScoutingTags: [],
  helpModeEnabled: true,
  customLabels: {},
  customCategories: {},
}

onMounted(() => {
  if (authStore.currentUser) {
    const userSettings = authStore.currentUser.settings
    localSettings.value = {
      ...defaultSettings,
      ...(userSettings ? JSON.parse(JSON.stringify(userSettings)) : {}),
    }
  }
})

// Handles the background push to Supabase
const silentCloudSync = async () => {
  if (!props.gameId || !props.coachInitials || !localSettings.value) return

  try {
    const snapshotSettings = {
      customLabels: localSettings.value.customLabels || {},
      customCategories: localSettings.value.customCategories || {},
      hiddenScoutingTags: localSettings.value.hiddenScoutingTags || []
    }

    const { error: settingsError } = await supabase
      .from('demo_settings')
      .upsert(
        {
          game_id: props.gameId,
          coach_initials: props.coachInitials,
          custom_labels: snapshotSettings.customLabels,
          custom_categories: snapshotSettings.customCategories,
          hidden_scouting_tags: snapshotSettings.hiddenScoutingTags,
          updated_at: new Date().toISOString()
        },
        { onConflict: 'game_id, coach_initials' }
      )

    if (settingsError) {
      console.warn('Silent cloud sync failed:', settingsError.message)
    }
  } catch (err) {
    console.warn('Silent cloud sync error:', err)
  }
}

const saveSettings = async () => {
  if (localSettings.value) {
    await authStore.updateSettings(localSettings.value)

    // 2. Attempt background cloud sync if props are provided
    await silentCloudSync()
  }
}

const isHidden = (arrayName: keyof UserSettings, value: string | number) => {
  if (!localSettings.value) return false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const arrayRef = localSettings.value[arrayName] as any[]
  return arrayRef ? arrayRef.includes(value) : false
}

const toggleArrayItem = async (arrayName: keyof UserSettings, value: string | number) => {
  if (!localSettings.value) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const arrayRef = localSettings.value[arrayName] as any[]

  const index = arrayRef.indexOf(value)
  if (index > -1) {
    arrayRef.splice(index, 1)
  } else {
    arrayRef.push(value)
  }
  await saveSettings()
}

const setCustomLabel = async (key: string, val: string | number | null) => {
  if (!localSettings.value) return
  if (!val) {
    delete localSettings.value.customLabels[String(key)]
  } else {
    localSettings.value.customLabels[String(key)] = String(val)
  }
  await saveSettings()
}

const setCustomCategory = async (key: string, val: string) => {
  if (!localSettings.value) return
  if (!localSettings.value.customCategories) localSettings.value.customCategories = {}
  localSettings.value.customCategories[key] = val
  await saveSettings()
}
</script>
